import * as React from 'react'
import { useTranslate } from 'react-admin'
import '../../../../utils/constants/CSS/eventForms.css'
import { Field, Form, Formik } from 'formik'
import { useState } from 'react'
import PropTypes from 'prop-types'
import FormContainer from '../../../layout/FormContainer/FormContainer'
import GetFormDirtyValue from '../../../getFormDirtyValue/getFormDirtyValue'
import MySelectField from '../../fields/MySelectField/MySelectField'
import parseEventFormSubmit from '../../../../utils/helpers/eventHelpers/parseEventFormSubmit'
import MyGroupIcon from '../../../../assets/icons/myGroupIcon'
import AutoChangeFakeEvent from '../../AutoChangeFakeEvent'
import MyLocationOnIcon from '../../../../assets/icons/myLocationOnIcon'
import { USER_ADDONS } from '../../../../utils/constants/userAddons'
import IsUserAddonsAuthorized from '../../../IsUserAddonsAuthorized/IsUserAddonsAuthorized'
import useEventFormsValidations from '../../../../hooks/useEventFormsValidations'
import { isFormFieldDisabled } from '../../../../utils/helpers/isFieldDisabled'
import onEventFieldChange from '../../../../utils/helpers/eventHelpers/onEventFieldChange'
import MyDateField from '../../fields/MyDateField'
import MyTimeField from '../../fields/MyTimeField'
import MyMultilineTextField from '../../fields/MyMultilineTextField'
import MyAutocomplete from '../../fields/MyAutocomplete'
import mutationType from '../../../../types/mutationType'
import teamType from '../../../../types/teamType'
import courtType from '../../../../types/courtType'
import eventVariantType from '../../../../types/events/eventVariantType'
import calendarDateClickType from '../../../../types/calendarDateClickType'
import eventRawType from '../../../../types/events/eventRawType'
import eventType from '../../../../types/events/eventType'
import EventFormLivestreamFields from '../../../EventFormLivestreamFields/EventFormLivestreamFields'
import GAME_EVENT_FORM_BROADCAST_FIELDS from '../../../../utils/constants/gameEventFormBroadcastFields'
import isCourtLivestreamEnabled from '../../../../utils/helpers/isCourtLivestreamEnabled'
import parseGameEventFormSubmit from '../../../../utils/helpers/eventHelpers/parseGameEventFormSubmit'
import {
  setEventEndTime,
  setEventStartTime,
} from '../../../../utils/helpers/eventHelpers/setEventTime'
import {
  PRACTICE,
  LIVESTREAM_TYPE_OPTIONS_MAPPER,
} from '../../../../utils/constants/livestreamTypeOptionsMapper'
import ConditionalRender from '../../../ConditionalRender/ConditionalRender'
import AddButton from '../../../ui/AddButton/AddButton'
import AddCourtModal from '../../../ui/AddCourtModal/AddCourtModal'
import localStorageHelper from '../../../../utils/helpers/localStorageHelper'
import localStorageIds from '../../../../utils/constants/localStorageIds'
import ids from '../../../../utils/constants/introIds'
import MyCourtIcon from '../../../../assets/icons/MyCourtIcon'
import { canUseOrgYoutube } from '../../../../utils/helpers/youtubeLink'
import USER_PERMISSIONS from '../../../../utils/constants/userPermissions'
import hasPermission from '../../../../utils/helpers/hasPermission'

const PracticeEventForm = (props) => {
  const {
    clickedEventId,
    createEvent,
    updateEvent,
    event,
    selectedEventType,
    setFormDirty,
    currentTeam,
    teams,
    dateClick,
    eventsRaw,
    setEvents,
    courts,
  } = props
  const { place, court, notes, eventTeams, topic, broadcast, ytBroadcastDetails } =
    event || {}
  const {
    id,
    streamDestination,
    streamDestination2,
    state,
    streamerSaveUrl,
    type,
    gameStateTrackingType,
    autofollowEnabled,
  } = broadcast || {}
  const gameStateTrackingAuto = gameStateTrackingType === 'Auto'
  const gameStateTrackingManual = gameStateTrackingType === 'Manual'

  const { broadcastId, thumbnailUrl, broadcastTitle, visibility } = ytBroadcastDetails || {}
  const [isCourtModalOpen, setIsCourtModalOpen] = useState(false)
  const orgLocalStorageHelper = localStorageHelper(localStorageIds.SELECTED_ORG)
  const hasSetYoutube = canUseOrgYoutube(orgLocalStorageHelper.localStorageValue, broadcastId)
  const useOrgYoutube = broadcastId || !streamDestination
  const translate = useTranslate()
  const { createLivestreamEventValidations, editLivestreamEventValidations } =
    useEventFormsValidations('practice', id, hasSetYoutube, useOrgYoutube)
  const { onFromDateFieldChange, onStartTimeFieldChange, onEndTimeFieldChange } = onEventFieldChange
  const allowOrgYoutube =
    hasSetYoutube &&
    hasPermission(USER_PERMISSIONS.UPDATE_EVENT)

  let teamContent
  if (currentTeam) {
    if (event) {
      teamContent = <span>{event.teams?.map((team) => team.name).join(', ')}</span>
    } else {
      teamContent = <span>{currentTeam.name}</span>
    }
  } else {
    teamContent = (
      <Field
        name='teams'
        label={translate('ra.formFields.teams')}
        multiple
        required
        component={MyAutocomplete}
        options={teams || []}
        getOptionLabel={(option) => option.name}
        defaultValue={event?.teams}
        fullWidth
        disabled={isFormFieldDisabled(state)}
      />
    )
  }

  return (
    <>
      <Formik
        validateOnChange={false}
        validateOnBlur={false}
        validate={
          clickedEventId ? editLivestreamEventValidations : createLivestreamEventValidations
        }
        initialValues={{
          fromDate: setEventStartTime(event, dateClick),
          startTime: setEventStartTime(event, dateClick),
          endTime: setEventEndTime(event, dateClick),
          // No team picked (org calendar, or an org with no teams yet): start empty, not
          // [undefined]/[false], or submit crashes reading the missing team.
          teams: event?.teams ?? (currentTeam ? [currentTeam] : []),
          place: place ?? 'Court',
          // New events: when the org has a single court there is nothing to choose, preselect it.
          courtId: court?.id ?? (!event && courts?.length === 1 ? courts[0].id : ''),
          topic: topic ?? '',
          notes: notes ?? '',
          autofollowEnabled: autofollowEnabled ?? true,
          [GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST]:
            !!streamDestination || !!streamDestination2,
          [GAME_EVENT_FORM_BROADCAST_FIELDS.IS_RECORD]: !!streamerSaveUrl,
          [GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE]: LIVESTREAM_TYPE_OPTIONS_MAPPER[PRACTICE](type),
          [GAME_EVENT_FORM_BROADCAST_FIELDS.STREAM_DESTINATION]: !useOrgYoutube
            ? streamDestination
            : null,
          [GAME_EVENT_FORM_BROADCAST_FIELDS.IS_ADDITIONAL_STREAM_DESTINATION_ADDED]:
            !!streamDestination2,
          [GAME_EVENT_FORM_BROADCAST_FIELDS.STREAM_DESTINATION_2]: streamDestination2 ?? null,
          [GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG]:
            !useOrgYoutube || !allowOrgYoutube
              ? null
              : {
                  broadcastId: broadcastId ?? null,
                  [GAME_EVENT_FORM_BROADCAST_FIELDS.YOUTUBE_THUMBNAIL]: thumbnailUrl ?? null,
                  [GAME_EVENT_FORM_BROADCAST_FIELDS.BROADCAST_TITLE]: broadcastTitle ?? '',
                  [GAME_EVENT_FORM_BROADCAST_FIELDS.YOUTUBE_VISIBILITY]: visibility ?? '',
                },
          [GAME_EVENT_FORM_BROADCAST_FIELDS.GAME_STATE_TRACKING_AUTO]: gameStateTrackingAuto,
          [GAME_EVENT_FORM_BROADCAST_FIELDS.GAME_STATE_TRACKING_MANUAL]: gameStateTrackingManual,
        }}
        onSubmit={(data) => {
          if (event) {
            updateEvent.mutate({
              formData: parseEventFormSubmit(
                parseGameEventFormSubmit(data, courts, selectedEventType),
              ),
              eventId: clickedEventId,
              eventType: selectedEventType,
            })
          } else {
            createEvent.mutate({
              formData: parseEventFormSubmit(
                parseGameEventFormSubmit(data, courts, selectedEventType),
              ),
              eventType: selectedEventType,
            })
          }
        }}
      >
        {({ values, setFieldValue }) => (
          <Form id={ids.createPractice.form}>
            <FormContainer>
              <MyDateField
                id={ids.createPractice.date}
                name='fromDate'
                label={translate('ra.formFields.date')}
                required
                disabled={isFormFieldDisabled(state)}
                onChange={onFromDateFieldChange}
              />
              <div
                id={ids.createPractice.time}
                className='div-flex-simple div-flex-responsive-1100'
              >
                <MyTimeField
                  name='startTime'
                  label={translate('ra.formFields.startTime')}
                  required
                  disabled={isFormFieldDisabled(state)}
                  onChange={onStartTimeFieldChange}
                />
                <MyTimeField
                  name='endTime'
                  label={translate('ra.formFields.endTime')}
                  required
                  disabled={isFormFieldDisabled(state, 'endTime')}
                  onChange={onEndTimeFieldChange}
                />
              </div>
              <div id={ids.createPractice.team} className='div-flex'>
                <MyGroupIcon />
                {teamContent}
              </div>

              <div className='div-flex'>
                <MyLocationOnIcon className='icon' />
                <MySelectField
                  name='place'
                  label={translate('ra.formFields.place')}
                  options={[
                    translate('ra.formFields.court'),
                    translate('ra.formFields.gym'),
                    translate('ra.formFields.other'),
                  ]}
                  disabled={isFormFieldDisabled(state)}
                />
              </div>
              {values.place === 'Court' || place === 'Court' ? (
                <div id={ids.createPractice.courtName} className='div-flex'>
                  <MyCourtIcon />
                  <MySelectField
                    name='courtId'
                    label={translate('ra.formFields.courtName')}
                    options={courts}
                    emptyText={translate('ra.text.emptyCourts')}
                    fullWidth
                    disabled={isFormFieldDisabled(state)}
                    addOptionsComponent={
                      <AddButton
                        onClick={() => setIsCourtModalOpen(true)}
                        label={translate('ra.action.add_court')}
                        noHoverEffect={courts?.length}
                        style={{
                          paddingLeft: !courts?.length ? 8 : 4,
                          marginRight: !courts?.length ? 10 : 0,
                        }}
                      />
                    }
                  />

                  <AddCourtModal
                    isCourtModalOpen={isCourtModalOpen}
                    setIsCourtModalOpen={setIsCourtModalOpen}
                    setValue={(newCourtValue) => setFieldValue('courtId', newCourtValue)}
                  />
                </div>
              ) : null}
              <MyMultilineTextField
                name='notes'
                label={translate('ra.formFields.notes')}
                multiline
                minRows={5}
                fullWidth
                disabled={isFormFieldDisabled(state)}
              />

              <ConditionalRender renderValue={isCourtLivestreamEnabled(courts, values?.courtId)}>
                <IsUserAddonsAuthorized acceptedAddons={[USER_ADDONS.LIVESTREAM]}>
                  <EventFormLivestreamFields
                    state={state}
                    allowOrgYoutube={allowOrgYoutube}
                    broadcastId={broadcastId ?? null}
                    selectedEventType={selectedEventType}
                  />
                </IsUserAddonsAuthorized>
              </ConditionalRender>
            </FormContainer>

            <GetFormDirtyValue setFormDirty={setFormDirty} />
            <AutoChangeFakeEvent
              clickedEventId={clickedEventId}
              selectedEventType={selectedEventType}
              eventsRaw={eventsRaw}
              setEvents={setEvents}
            />
          </Form>
        )}
      </Formik>
    </>
  )
}

PracticeEventForm.propTypes = {
  clickedEventId: PropTypes.string,
  createEvent: mutationType,
  updateEvent: mutationType,
  event: eventType,
  selectedEventType: eventVariantType,
  setFormDirty: PropTypes.func,
  currentTeam: teamType,
  teams: PropTypes.arrayOf(teamType),
  dateClick: calendarDateClickType,
  eventsRaw: PropTypes.arrayOf(eventRawType),
  setEvents: PropTypes.func,
  courts: PropTypes.arrayOf(courtType),
}

export default PracticeEventForm
